/**
 * メーカー宛メール送信モーダル（見積依頼 / 正式発注）。
 *
 * 旧「発注 + 正式発注メール」等の mailto: 起動を廃止し、見積書のメール送信
 * (DocumentMailDialog) と同じ「編集して送信」体験に統一するための共通モーダル。
 *
 * フロー:
 *  1. open 時に prepareManufacturerMail Callable で初期値ロード
 *     （宛先=カテゴリーのメーカーメール / 件名・本文=テンプレ or 既定文面 / 送信元）
 *  2. 送信元・宛先・CC・件名・本文を編集可能
 *  3. 送信ボタン → sendManufacturerMail で送信
 */
import { Loader2, Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { EmailChipsInput } from '@/components/common/EmailChipsInput';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import {
  usePrepareManufacturerMail,
  useSendManufacturerMail,
  type ManufacturerMailKind,
} from '@/hooks/useManufacturerQuotes';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  kind: ManufacturerMailKind;
  manufacturerQuoteId?: string | null;
  poId?: string | null;
}

export function ManufacturerMailDialog({
  open,
  onOpenChange,
  dealId,
  kind,
  manufacturerQuoteId,
  poId,
}: Props) {
  const label = kind === 'quote_request' ? '見積依頼' : '正式発注';

  const { data: accounts } = useMailAccounts();
  const prepare = usePrepareManufacturerMail();
  const send = useSendManufacturerMail();

  const [loading, setLoading] = useState(false);
  const [toList, setToList] = useState<string[]>([]);
  const [fromAccountId, setFromAccountId] = useState('');
  const [ccList, setCcList] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const activeAccounts = (accounts ?? []).filter((a) => a.status === 'active');

  // open 時に prepare で初期値ロード（同一 key で1回のみ）
  const loadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const key = `${kind}:${dealId}:${manufacturerQuoteId ?? ''}:${poId ?? ''}`;
    if (loadedRef.current === key) return;
    loadedRef.current = key;
    setLoading(true);
    prepare
      .mutateAsync({ dealId, kind, manufacturerQuoteId, poId })
      .then((r) => {
        setToList(r.defaultTo ?? []);
        setFromAccountId(r.fromAccountId ?? '');
        setSubject(r.subject ?? '');
        setBody(r.body ?? '');
        setCcList(r.defaultCc ?? []);
      })
      .catch((err) => {
        toast.error(`メール内容の取得に失敗しました: ${(err as Error).message}`);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId, kind, manufacturerQuoteId, poId]);

  // close 時にロード済みフラグをリセット（次回開いたとき再取得）
  useEffect(() => {
    if (!open) loadedRef.current = null;
  }, [open]);

  const handleSend = async () => {
    if (toList.length === 0) {
      toast.error('宛先を入力してください');
      return;
    }
    try {
      const r = await send.mutateAsync({
        dealId,
        to: toList,
        cc: ccList,
        subject,
        body,
        fromAccountId: fromAccountId || null,
        poId: poId ?? null,
        kind,
      });
      if (r.ok) {
        toast.success(`メーカーへ${label}メールを送信しました`);
        onOpenChange(false);
      } else {
        toast.error(`送信できませんでした: ${r.reason ?? '理由不明'}`);
      }
    } catch (err) {
      toast.error(`送信失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>メーカーへ{label}メールを送信</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
            <Loader2 className="size-5 animate-spin mr-2" />
            メール内容を読み込み中…
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs">送信元</Label>
              <select
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
                className="flex h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-focus)] focus:outline-none"
              >
                {activeAccounts.length === 0 && (
                  <option value="">送信可能なアカウントがありません</option>
                )}
                {activeAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName ? `${a.displayName} <${a.email}>` : a.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs">宛先（メーカー）</Label>
              <EmailChipsInput
                value={toList}
                onChange={setToList}
                placeholder="manufacturer@example.com（Enter / カンマで複数）"
              />
              {toList.length === 0 && (
                <p className="text-[11px] text-[var(--accent-amber)] mt-1">
                  カテゴリー設定にメーカー（発注先）メールアドレスが未登録です。宛先を直接入力してください。
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">CC</Label>
              <EmailChipsInput
                value={ccList}
                onChange={setCcList}
                placeholder="CCを追加（Enter / カンマ区切り）"
              />
            </div>

            <div>
              <Label className="text-xs">件名</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div>
              <Label className="text-xs">本文</Label>
              <Textarea
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-mono text-xs"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={send.isPending}
          >
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={loading || send.isPending || toList.length === 0}
          >
            <Mail className="size-3.5" />
            {send.isPending ? '送信中…' : '送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
